/**
 * «Юниверситет» (28.09): треки квеста — названия в тексте становятся кнопками ▶ (как
 * MD.linkTracks в assets/md.js базы знаний). Плеер — ОДИН Audio на всю вкладку:
 * клик по играющей кнопке — стоп, по другой — переключение; preload="none"; уход с
 * вкладки — тишина. «Меломан» считает РАЗНЫЕ файлы и только после успешного play()
 * (в макете счётчик рос на каждый клик, включая «стоп»).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { UniTrack } from "@ub/shared/uni";
import { useMediaUrl } from "./ctx";
import css from "./uni.module.css";

export interface TrackApi {
  playing: string | null;
  failed: ReadonlySet<string>;
  toggle: (file: string) => void;
  stop: () => void;
  /** регулярка всех названий, длинные первыми («A Moment's Respite II» раньше «…I») */
  re: RegExp | null;
  find: (title: string) => UniTrack | undefined;
}

const Ctx = createContext<TrackApi | null>(null);
export const useTracks = () => useContext(Ctx);

/** Ключ поиска: без регистра, типографские апострофы = прямой. */
const keyOf = (s: string) => s.toLowerCase().replace(/[’`]/g, "'");

function trackRegex(tracks: UniTrack[]): RegExp | null {
  const titles = [...new Set(tracks.map((t) => t.title).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!titles.length) return null;
  const alt = titles
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/['’`]/g, "['’`]"))
    .join("|");
  /* границы — не буква латиницы или кириллицы (как в md.js). Без lookbehind: его нет в Safari/WKWebView
     до iOS 16.4 — там RegExp бросал бы при рендере и гасил весь кабинет. Левая граница — группа 1. */
  try { return new RegExp(`(^|[^A-Za-zА-Яа-яЁё])(${alt})(?![A-Za-zА-Яа-яЁё])`, "gi"); } catch { return null; }
}

export function TrackPlayerProvider({ tracks, onPlayed, children }: {
  tracks: UniTrack[];
  onPlayed: (file: string) => void;
  children: ReactNode;
}) {
  const url = useMediaUrl();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const req = useRef(0);
  const curFile = useRef<string | null>(null);
  const playedRef = useRef(onPlayed);
  playedRef.current = onPlayed;
  const [playing, setPlaying] = useState<string | null>(null);
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());

  const markFailed = useCallback((file: string, bad: boolean) => {
    setFailed((prev) => {
      if (prev.has(file) === bad) return prev;
      const next = new Set(prev);
      if (bad) next.add(file); else next.delete(file);
      return next;
    });
  }, []);

  const stop = useCallback(() => {
    req.current += 1;
    curFile.current = null;
    audioRef.current?.pause();
    setPlaying(null);
  }, []);

  const toggle = useCallback((file: string) => {
    if (curFile.current === file) { stop(); return; }
    let a = audioRef.current;
    if (!a) {
      a = new Audio();
      a.preload = "none";
      a.addEventListener("ended", () => { curFile.current = null; setPlaying(null); });
      a.addEventListener("error", () => {
        const f = curFile.current;
        if (!f || !a?.getAttribute("src")) return;
        curFile.current = null;
        setPlaying(null);
        markFailed(f, true);
      });
      audioRef.current = a;
    }
    a.pause();
    const my = ++req.current;
    const src = url(`tracks/${file}`);
    markFailed(file, false);
    if (!src) { curFile.current = null; setPlaying(null); markFailed(file, true); return; }
    curFile.current = file;
    a.src = src;
    setPlaying(file);
    a.play()
      .then(() => playedRef.current(file))
      .catch((e: unknown) => {
        if (my !== req.current) return; // переключили на другой трек — это не ошибка
        if (e instanceof DOMException && e.name === "AbortError") return;
        curFile.current = null;
        setPlaying(null);
        markFailed(file, true);
      });
  }, [url, stop, markFailed]);

  /* Уход с вкладки — музыка замолкает и отпускает соединение. */
  useEffect(() => () => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    a.removeAttribute("src");
    a.load();
  }, []);

  const re = useMemo(() => trackRegex(tracks), [tracks]);
  const byKey = useMemo(() => new Map(tracks.map((t) => [keyOf(t.title), t])), [tracks]);
  const find = useCallback((title: string) => byKey.get(keyOf(title)), [byKey]);

  const value = useMemo<TrackApi>(() => ({ playing, failed, toggle, stop, re, find }), [playing, failed, toggle, stop, re, find]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Кнопка трека в тексте: ▶ название; играет — ■ и заливка. */
export function TrackButton({ file, label }: { file: string; label: string }) {
  const t = useTracks();
  const on = t?.playing === file;
  const bad = !!t?.failed.has(file);
  return (
    <button
      type="button"
      className={`${css.trk} ${on ? css.trkOn : ""} ${bad ? css.trkErr : ""}`}
      aria-pressed={on}
      title={bad ? "Трек не включился — нажми ещё раз" : on ? "Остановить" : "Включить трек"}
      onClick={(e) => { e.stopPropagation(); t?.toggle(file); }}
    >
      {label}
    </button>
  );
}
